import React, { useEffect, useState } from 'react';
import { Navbar } from './Navbar';
import { Footer } from './Footer';
import './Results.css';

// Simple CountUp Hook
const useCountUp = (end, duration = 2000) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    let startTime = null;
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);

      // Easing function for smoother finish
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);

      setCount(Math.floor(easeOutQuart * end));

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    requestAnimationFrame(animate);
  }, [end, duration]);

  return count;
};

export const Results = ({
  analysisResult,
  onViewFullAnalysis,
  onGoToSignup,
  user,
  onSignInClick
}) => {
  const rawLeft = analysisResult?.left_percentage || 50;
  const leftPercentage = Math.round(rawLeft);
  const rightPercentage = 100 - leftPercentage;

  // Animated values
  const animatedLeft = useCountUp(leftPercentage);
  const animatedRight = useCountUp(rightPercentage);

  // Determine Archetype
  const getArchetype = () => {
    const difference = Math.abs(leftPercentage - rightPercentage);

    if (difference < 10) {
      return {
        name: "The Alchemist ⚖️",
        desc: "master of balance",
        gradient: "linear-gradient(135deg, #a855f7, #6366f1)"
      };
    } else if (leftPercentage > rightPercentage) {
      return {
        name: "The Strategist 🧠",
        desc: "architect of logic",
        gradient: "linear-gradient(135deg, #3b82f6, #60a5fa)"
      };
    } else {
      return {
        name: "The Visionary 🎨",
        desc: "creator of new worlds",
        gradient: "linear-gradient(135deg, #ec4899, #f472b6)"
      };
    }
  };

  const archetype = getArchetype();

  return (
    <div className="results-page base-background">
      <div className="noise-overlay" />
      {/* Ambient decorative orbs to fill visual space */}
      <div className="ambient-orbs">
        <div className="orb orb-1" />
        <div className="orb orb-2" />
        <div className="orb orb-3" />
      </div>
      <Navbar
        currentUser={user}
        onSignInClick={onSignInClick}
      />
      <div className="results-container">
        <div className="results-header-block animate-slide-down">
          <h1 className="results-headline">Profile Sneak Peek 👀</h1>
          <p className="results-subheadline">This is just a fraction of what we found.</p>
        </div>
        <div
          className="brain-balance-section card-hover-effect animate-fade-up-1"
        >
          <div className="archetype-badge">
            <h2 className="archetype-name" style={{ backgroundImage: archetype.gradient }}>{archetype.name}</h2>
            <p className="archetype-desc">{archetype.desc}</p>
          </div>
          <div className="brain-sides">
            <span className="left-label">LEFT BRAIN</span>
            <span className="right-label">RIGHT BRAIN</span>
          </div>

          <div className="balance-slider">
            <div className="slider-track">
              <div
                className="slider-fill"
                style={{
                  background: `linear-gradient(90deg, #3b82f6 0%, #ec4899 100%)`,
                  width: '100%',
                  transition: 'width 1.5s ease-out'
                }}
              />
              <div
                className="slider-indicator"
                style={{
                  left: `${rightPercentage}%`,
                  transform: 'translateX(-50%)',
                  transition: 'left 1.5s cubic-bezier(0.34, 1.56, 0.64, 1)'
                }}
              />
            </div>
          </div>

          <div className="slider-labels">
            <span className="logic-label">Logic</span>
            <span className="balance-center">Balance</span>
            <span className="intuition-label">Intuition</span>
          </div>

          <div className="percentage-display">
            <div className="left-percentage">
              <span className="percentage-number blue">{animatedLeft}%</span>
              <span className="percentage-label">Analytical</span>
            </div>
            <div className="right-percentage">
              <span className="percentage-number pink">{animatedRight}%</span>
              <span className="percentage-label">Creative</span>
            </div>
          </div>

          {/* Moved Traits Inside Card for Single Focus Point */}
          {analysisResult.trait_analysis?.top_traits?.length > 0 && (
            <div className="traits-preview internal-traits">
              <div className="preview-strengths" style={{ justifyContent: 'center' }}>
                {analysisResult.trait_analysis.top_traits.slice(0, 3).map((trait, index) => (
                  <span key={index} className="preview-strength-pill">
                    ✦&nbsp;&nbsp;{trait.charAt(0).toUpperCase() + trait.slice(1)}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Locked Deep Analysis Hook */}
          <div className="preview-career blurred-text insight-box" style={{ marginTop: '0.8rem' }}>
            <span className="preview-career-label">💡 AI COACH INSIGHT</span>
            <span className="preview-career-value insight-text">
              Your ideal career path and hidden relationship blindspots are calculated and ready.
            </span>
          </div>

          <div className="preview-frosted-overlay results-frosted">
            <span className="preview-unlock-text">Deep Analysis Locked</span>
          </div>
        </div>

        {/* CTA Button - Single focused conversion */}
        <div className="results-actions animate-fade-up-2">
          <p className="cta-teaser">Upload your handwriting to unlock the full analysis</p>
          <button className="glass-cta" onClick={onGoToSignup} style={{ marginTop: '0.5rem', width: '100%', justifyContent: 'center', padding: '1rem' }}>
            <span className="cta-sparkle">✍️</span>
            <span className="cta-text">Sign Up & Upload Handwriting</span>
          </button>
          <p className="terms-inline-disclaimer">
            By signing up you agree to our{' '}
            <a href="/terms" target="_blank" rel="noopener noreferrer">Terms</a>
            {' '}&amp;{' '}
            <a href="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
          </p>
        </div>
      </div>

      <Footer />
    </div>
  );
};